
import React from 'react';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import ReferralCodeDisplay from '@/components/ReferralCodeDisplay';
import ReferralQRCode from '@/components/ReferralQRCode';
import PrintableReferralCard from '@/components/PrintableReferralCard';
import NotificationSettings from './NotificationSettings';
import { Copy, Share2, Bell, QrCode, CreditCard } from 'lucide-react';

interface ReferralLinkCardProps {
  referralCode: string;
  referralLink: string;
  userName: string;
  userId: string;
}

const ReferralLinkCard: React.FC<ReferralLinkCardProps> = ({
  referralCode,
  referralLink,
  userName,
  userId
}) => {
  const { toast } = useToast();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast({
      title: "Link copied",
      description: "Referral link copied to clipboard",
    });
  };

  return (
    <Card className="mt-2 border-0 shadow-none bg-transparent">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 mb-1">
          <Share2 className="h-5 w-5 text-blue-500" />
          <CardTitle className="text-xl text-blue-700">Your Referral Link</CardTitle>
        </div>
        <CardDescription className="text-blue-600/70">Share this link to start earning points</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <ReferralCodeDisplay referralCode={referralCode} />
        
        <div className="relative group">
          <Input
            value={referralLink}
            readOnly
            className="pr-20 bg-white border-blue-200 focus:border-blue-400 transition-colors"
          />
          <Button 
            className="absolute right-0 top-0 h-full rounded-l-none bg-blue-600 hover:bg-blue-700 transition-colors flex items-center gap-1"
            onClick={handleCopyLink}
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-3 text-blue-700">
            <QrCode className="h-5 w-5" />
            <h3 className="font-semibold">Scan to Share</h3>
          </div>
          <ReferralQRCode referralLink={referralLink} />
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-3 text-blue-700">
            <CreditCard className="h-5 w-5" />
            <h3 className="font-semibold">Business Card</h3>
          </div>
          <PrintableReferralCard 
            referralLink={referralLink}
            referralCode={referralCode}
            referrerName={userName}
          />
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-3 text-blue-700">
            <Bell className="h-5 w-5" />
            <h3 className="font-semibold">Notification Preferences</h3>
          </div>
          <NotificationSettings userId={userId} />
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralLinkCard;
